<app-page-breadcrumb pageTitle="Configuración de Auditoría" />

<div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 shadow-sm">
  <div class="p-6">

    <!-- Header -->
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Configuración por Entidad</h2>
      <button
        *appHasPermission="'audit:edit'"
        (click)="reloadCache()"
        [disabled]="reloading"
        title="Vuelve a leer esta configuración desde la base de datos y la aplica de inmediato en el servidor, sin esperar ni reiniciar. Úsalo después de editar una entidad."
        class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
        <svg class="h-4 w-4" [class.animate-spin]="reloading" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        {{ reloading ? 'Recargando...' : 'Recargar caché' }}
      </button>
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center justify-center py-12">
        <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    }

    <!-- Table -->
    @if (!loading && !error) {
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <th class="py-3 px-4">Entidad</th>
              <th class="py-3 px-4">Módulo</th>
              <th class="py-3 px-4 text-center">Habilitado</th>
              <th class="py-3 px-4">Nivel</th>
              <th class="py-3 px-4">Retención (días)</th>
              <th class="py-3 px-4">Campos sensibles</th>
              <th class="py-3 px-4">Campos excluidos</th>
              <th class="py-3 px-4 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            @for (s of settings; track s.id) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td class="py-3 px-4 font-medium text-gray-800 dark:text-white" [title]="s.entityName">{{ display.translateEntity(s.entityName) }}</td>
                <td class="py-3 px-4 text-gray-600 dark:text-gray-300" [title]="s.moduleName">{{ display.translateModule(s.moduleName) }}</td>
                <td class="py-3 px-4 text-center">
                  @if (s.enabled) {
                    <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/10">
                      <svg class="h-3 w-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </span>
                  } @else {
                    <span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-500/10">
                      <svg class="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                      </svg>
                    </span>
                  }
                </td>
                <td class="py-3 px-4">
                  <span class="rounded-full px-2.5 py-0.5 text-xs font-medium" [ngClass]="getAuditLevelClass(s.auditLevel)"
                    [title]="display.auditLevelDescription(s.auditLevel)">
                    {{ display.auditLevelLabel(s.auditLevel) }}
                  </span>
                </td>
                <td class="py-3 px-4 text-gray-600 dark:text-gray-300">{{ s.retentionDays }}</td>
                <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs max-w-[150px] truncate">
                  {{ s.sensitiveFields && s.sensitiveFields.length > 0 ? s.sensitiveFields.join(', ') : '—' }}
                </td>
                <td class="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs max-w-[150px] truncate">
                  {{ s.excludedFields && s.excludedFields.length > 0 ? s.excludedFields.join(', ') : '—' }}
                </td>
                <td class="py-3 px-4 text-center">
                  <button
                    (click)="openEdit(s)"
                    [appDisableWithoutPermission]="'audit:edit'"
                    class="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                </td>
              </tr>
            }
            @empty {
              <tr>
                <td colspan="8" class="py-10 text-center text-gray-400 dark:text-gray-500">
                  No hay configuraciones de auditoría.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>

<!-- ===================== MODAL: EDITAR SETTING ===================== -->
@if (showEditModal && editingSetting) {
  <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4" (click)="showEditModal = false">
    <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
      <h3 class="mb-5 text-lg font-semibold text-gray-800 dark:text-white">
        Editar Auditoría — {{ display.translateEntity(editingSetting.entityName) }}
      </h3>

      <div class="space-y-4">
        <!-- Enabled -->
        <div class="flex items-center gap-3">
          <label class="relative inline-flex cursor-pointer items-center">
            <input type="checkbox" [(ngModel)]="editForm.enabled" class="peer sr-only" />
            <div class="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-700"></div>
          </label>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Auditoría habilitada</span>
        </div>

        <!-- Audit Level -->
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Nivel de auditoría</label>
          <select
            [(ngModel)]="editForm.auditLevel"
            class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white">
            @for (level of auditLevelOptions; track level) {
              <option [value]="level">{{ display.auditLevelDescription(level) }}</option>
            }
          </select>
        </div>

        <!-- Retention Days -->
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Días de retención</label>
          <input
            type="number"
            [(ngModel)]="editForm.retentionDays"
            min="1"
            max="3650"
            class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
          <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Entre 1 y 3650 días</p>
        </div>

        <!-- Sensitive Fields -->
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Campos sensibles</label>
          <input
            type="text"
            [(ngModel)]="sensitiveFieldsText"
            placeholder="password, token, secret"
            class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
          <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Separados por coma. Se enmascaran en los registros.</p>
        </div>

        <!-- Excluded Fields -->
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Campos excluidos</label>
          <input
            type="text"
            [(ngModel)]="excludedFieldsText"
            placeholder="internalNote, tempField"
            class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
          <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Separados por coma. Se excluyen completamente.</p>
        </div>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <button
          (click)="showEditModal = false"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
          Cancelar
        </button>
        <button
          *appHasPermission="'audit:edit'"
          (click)="saveSetting()"
          [disabled]="saving"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
}
